// src/components/MobileBottomNav.tsx
//
// Instagram-style floating bottom navigation for mobile (Grant's design
// directive, 2026-07-11 — see TorchPass projects/LN-PRO-UI-ENHANCEMENT-LANE).
// A frosted, fully-rounded pill floating inset from the screen edges:
// 4 icon-only primary tabs + an avatar "More" slot that opens the overflow
// sheet (Insights / Settings / Admin). Replaces the hamburger-only mobile nav
// so every destination is ONE thumb-tap and the current tab is always visible.
//
// Theme-aware by rule: all colors come from theme.palette (works in the
// current theme and in the future light/dark toggle with zero changes here).

import React from "react";
import { alpha, Avatar, Badge, Box, IconButton, useTheme } from "@mui/material";

export interface BottomNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface MobileBottomNavProps {
  items: BottomNavItem[];
  currentPage: string;
  onNavigate: (page: string) => void;
  /** Opens the overflow sheet (the repurposed Drawer). Hidden when null. */
  onMore: (() => void) | null;
  /** True when the current page lives in the overflow sheet (e.g. Settings). */
  moreActive?: boolean;
  /** Single character shown in the More/account slot. */
  avatarInitial?: string;
  /** Show a red attention dot on this tab id (e.g. "clients" for new leads). */
  dotOn?: string | null;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  items,
  currentPage,
  onNavigate,
  onMore,
  moreActive = false,
  avatarInitial = "•",
  dotOn = null,
}) => {
  const theme = useTheme();

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        position: "fixed",
        left: 14,
        right: 14,
        bottom: `calc(env(safe-area-inset-bottom, 0px) + 14px)`,
        zIndex: theme.zIndex.appBar + 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        height: 60,
        maxWidth: 420,
        mx: "auto",
        px: 1,
        borderRadius: "30px",
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[8],
      }}
    >
      {items.map((item) => {
        const active = currentPage === item.id;
        const button = (
          <IconButton
            key={item.id}
            aria-label={item.label}
            aria-current={active ? "page" : undefined}
            onClick={() => onNavigate(item.id)}
            sx={{
              width: 46,
              height: 40,
              borderRadius: "12px",
              color: active ? "primary.main" : "text.secondary",
              backgroundColor: active
                ? alpha(theme.palette.primary.main, 0.14)
                : "transparent",
              "&:hover": {
                backgroundColor: active
                  ? alpha(theme.palette.primary.main, 0.2)
                  : alpha(theme.palette.text.primary, 0.06),
              },
            }}
          >
            {item.icon}
          </IconButton>
        );
        if (dotOn === item.id) {
          return (
            <Badge
              key={item.id}
              variant="dot"
              color="error"
              overlap="circular"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{ "& .MuiBadge-dot": { top: 6, right: 8 } }}
            >
              {button}
            </Badge>
          );
        }
        return button;
      })}

      {onMore && (
        <IconButton
          aria-label="More"
          onClick={onMore}
          sx={{
            width: 46,
            height: 40,
            borderRadius: "12px",
            backgroundColor: moreActive
              ? alpha(theme.palette.primary.main, 0.14)
              : "transparent",
          }}
        >
          <Avatar
            sx={{
              width: 27,
              height: 27,
              fontSize: 13,
              fontWeight: 700,
              bgcolor: moreActive
                ? "primary.main"
                : alpha(theme.palette.primary.main, 0.25),
              color: moreActive
                ? "primary.contrastText"
                : "primary.main",
            }}
          >
            {avatarInitial}
          </Avatar>
        </IconButton>
      )}
    </Box>
  );
};

export default MobileBottomNav;
